import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api, type TokenQuota } from '../services/api'
import {
  UsersIcon,
  ServiceIcon,
  UserIcon,
  KeyIcon,
  ChevronRightIcon
} from '../components/ui/Icons'
import { SkeletonStatCard } from '../components/ui/Skeleton'
import OnboardingHealthPanel from '../components/OnboardingHealthPanel'
import QuotaBookingsDrilldown from '../components/QuotaBookingsDrilldown'
import { isPersonalMode } from '../utils/cockpitMode'
import { MINUS_LIMIT_MIO, tokenAccountView } from '../utils/tokenAccount'
import { useToast } from '../components/ui'
import { forbiddenText } from '../lib/forbiddenText'
import { apiOrigin, isTenantInstance } from '../lib/runtimeConfig'
import './Dashboard.css'



// TQ #6966: dynamische Dashboard-News (news_posts) statt hardcoded Karten.
interface NewsPost {
  id: number
  published_at: string
  category: string
  headline: string
  body: string
  cta_label?: string | null
  cta_href?: string | null
}

interface Organization {
  id: number
  name: string
  slug: string
    public_token?: string
  member_role: string
}

interface DashboardData {
  organization_name: string
  member_count: number
  service_count: number
  license_count: number
  your_role: string
  pending_invites?: number
}

interface Props {
  organization: Organization
}

// Auftrag Marco/Ada (marco-assistenz-workspace), 2026-09-23: Token-Kontingent-Zeile
// im Cockpit fuer alle Kunden. 18 Mio Token = 1 Stunde Arbeitszeit ist fest und
// ratifiziert (kunden-workspace/Vorlage-Kontingent-Preisstaffel.md), nicht neu
// berechnen oder aendern.
const TOKENS_PER_HOUR = 18_000_000

function formatTokenAmount(n: number): string {
  if (n <= 0) return '0 Token'
  if (n >= 1_000_000) {
    const mio = Math.round((n / 1_000_000) * 10) / 10
    const str = Number.isInteger(mio) ? String(mio) : mio.toFixed(1).replace('.', ',')
    return `${str} Mio Token`
  }
  return `${Math.max(1, Math.round(n / 1000))}k Token`
}

function formatHours(hours: number): string {
  if (hours <= 0) return '0'
  if (hours < 0.1) return '< 0,1'
  return hours.toFixed(1).replace('.', ',')
}

interface AriaTokenBudget {
  tenant: string
  month: string
  /** Gesamt diesen Monat (haiku+sonnet+opus). Backward-compat. */
  sonnet_used: number
  sonnet_budget: number
  percent_used: number
  warning: boolean
  allowed_modules: string[]
  reset_date: string
  /** TQ #5133 — aufgesplittet pro Modell-Family ab 2026-05-19 */
  haiku_used?: number
  opus_used?: number
}

export default function Dashboard({ organization }: Props) {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [ariaTokenBudget, setAriaTokenBudget] = useState<AriaTokenBudget | null>(null)
  // Typ 2 (IT-Service-Kontingent, ERPNext) — getrennt von ariaTokenBudget (Typ 1,
  // Cortex-Aria-Workspace-Quota). Siehe ADR-Cockpit-Live-Daten-und-Token-Einheit.md.
  const [tokenQuota, setTokenQuota] = useState<TokenQuota | null>(null)
  const [tokenQuotaForbidden, setTokenQuotaForbidden] = useState<string | null>(null)
  const [overageLoading, setOverageLoading] = useState(false)
  const [news, setNews] = useState<NewsPost[]>([])
  const [newsLoading, setNewsLoading] = useState(true)
  const { token, user, saveSessionForExternalNavigate } = useAuth()
  const { showToast } = useToast()
  // TQ #6933: Personal-Mode (with.it-sce.de) → /api/me/dashboard + Personal-Layout
  // (Starter-Guide, Aria-CTA, News). Org-spezifische Blöcke conditional hidden.
  const personal = isPersonalMode()

  useEffect(() => {
    loadDashboard()
    // TQ #5463: Token-Card alle 60s im Hintergrund auffrischen, damit
    // Aria-Budget-Anzeige nicht hinter dem echten Verbrauch zurueck-laeuft
    // (besonders relevant nach TQ #5420 wenn JWT-Tenant von itsce auf
    // glasbrenner geswapped wird — Cache-State vom alten Tenant haette
    // sonst eine ganze Session ueberdauert).
    const interval = setInterval(() => {
      loadTokenBudget()
      loadTokenQuota()
    }, 60_000)
    return () => clearInterval(interval)
  }, [(organization.public_token || organization.slug), token])

  // TQ #6966: Dashboard-News dynamisch aus /api/public/news (public, kein Auth).
  // audience richtet sich nach Login-Kontext (Personal-Mode vs Org-Mode).
  // Fehler/leere Antwort → 0 Karten (Sektion blendet sich aus), kein harter Fail.
  useEffect(() => {
    let cancelled = false
    setNewsLoading(true)
    // Tenant-Instanz: /api/public/* ist zentral, cockpit-api hat es nicht (TQ #409).
    if (isTenantInstance()) { setNews([]); setNewsLoading(false); return }
    const audience = personal ? 'personal' : 'org'
    fetch(`${apiOrigin()}/api/public/news?audience=${audience}&limit=3`)
      .then(r => (r.ok ? r.json() : { news: [] }))
      .then(data => { if (!cancelled) setNews(Array.isArray(data.news) ? data.news : []) })
      .catch(() => { if (!cancelled) setNews([]) })
      .finally(() => { if (!cancelled) setNewsLoading(false) })
    return () => { cancelled = true }
  }, [personal])

  const loadTokenBudget = async () => {
    if (!token) return
    try {
      // TQ #7059: Personal-Mode (with.it-sce.de) nutzt den per-User-Endpoint
      // /api/v1/me/token-budget (per-User-Quota inkl. Override).
      // TQ #6899: Org-Detail-View nutzt den slug-adressierten Endpoint
      // /api/v1/orgs/<slug>/token-budget, der die Quota der ANGESCHAUTEN Org
      // liefert (slug aus dem Org-Kontext) — nicht mehr das JWT-scoped
      // /org/token-budget (effective_tenant), das bei IT-SCE-Staff-Sicht auf
      // eine fremde Org faelschlich die itsce-Quota zeigte.
      const endpoint = personal
        ? '/api/v1/me/token-budget'
        : `/api/v1/orgs/${encodeURIComponent(organization.slug)}/token-budget`
      // Glasbrenner-Cockpit-Fix (23.09.2026): api.getCortexBaseURL() statt
      // hardcodetem CORTEX_URL — liefert die per-Tenant aufgeloeste Origin
      // (main.tsx ruft CortexHostAPI.resolve() beim Mount auf), fällt bis
      // dahin/im Fehlerfall auf die geteilte Instanz zurück.
      const r = await fetch(`${api.getCortexBaseURL()}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        // TQ #5463: keine Browser-Caching der Token-Budget-Response. Vorher
        // konnte der Browser zwischen Tenant-Wechseln einen 200-Response
        // weiterservieren und der User sah den alten Pool-Wert (itsce-Leak
        // vor #5420). cache:'no-store' erzwingt frischen Roundtrip.
        cache: 'no-store',
      })
      if (!r.ok) return
      const data = await r.json()
      if (personal) {
        // /me/token-budget (MeTokenBudget) hat andere Feldnamen (used/budget)
        // → auf AriaTokenBudget mappen, damit das bestehende Rendering greift.
        // 'sonnet_used' ist hier — wie beim /org-Endpoint — der KOMBINIERTE
        // Monatsverbrauch (haiku+sonnet+opus, backward-compat-Naming); die
        // Sonnet-Family-Zeile rechnet sonnet_used - haiku_used - opus_used.
        setAriaTokenBudget({
          tenant: data.tenant,
          month: data.month,
          sonnet_used: data.used,
          sonnet_budget: data.budget,
          percent_used: data.percent_used,
          warning: data.warning,
          allowed_modules: data.allowed_modules,
          reset_date: data.reset_date,
          haiku_used: data.haiku_used,
          opus_used: data.opus_used,
        })
      } else {
        setAriaTokenBudget(data as AriaTokenBudget)
      }
    } catch { /* non-critical */ }
  }

  const loadDashboard = async () => {
    try {
      // Scout-Nachrichten-Card wurde entfernt — Cortex/Aria zaehlt Tokens
      // zentral via tenant_rate_configs. Die alte scout_usage-Tabelle ist
      // veralteter Pre-Aria-Mechanismus und sollte nicht parallel laufen.
      const [dashResponse] = await Promise.all([
        personal
          ? api.getMeDashboard()
          : api.getDashboard((organization.public_token || organization.slug)),
        loadTokenBudget(),
        loadTokenQuota(),
      ])
      setDashboard(dashResponse.dashboard)
    } catch (error) {
      console.error('Failed to load dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  // Typ 2 (IT-Service-Kontingent, ERPNext) — eigener Request, kein Cortex-State.
  // Personal-Mode hat kein ERPNext-Kundenkontingent (nur Orgs sind Kunden),
  // deshalb dort gar nicht erst anfragen. 404 (kein verknuepfter ERPNext-Kunde),
  // 502/503 (Read-Bot nicht erreichbar/konfiguriert) blenden die Zeile einfach
  // aus statt einen Fehler zu zeigen — bewusst still, analog loadTokenBudget.
  const loadTokenQuota = async () => {
    if (personal) return
    try {
      const data = await api.getOrgTokenQuota(organization.public_token || organization.slug)
      setTokenQuota(data)
      setTokenQuotaForbidden(null)
    } catch (e) {
      setTokenQuota(null)
      // Nur 403 (ERPNext-Rechte, TQ #890) zeigt den Backend-Text; alle anderen Fehler blenden weiter still aus.
      setTokenQuotaForbidden(forbiddenText(e))
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Inhaber'
      case 'admin': return 'Administrator'
      case 'member': return 'Mitglied'
      default: return role
    }
  }

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'owner': return 'Volle Kontrolle über die Organisation'
      case 'admin': return 'Kann Mitglieder und Services verwalten'
      case 'member': return 'Zugriff auf Services der Organisation'
      default: return ''
    }
  }

  // TQ #10852: Review-Fund — der urspruenglich gemeldete "API-404" existiert nicht
  // (live verifiziert: die Route antwortet 401 ohne Auth, nicht 404 — Route ist
  // korrekt registriert). Der reale Bug war zweifach: (1) der leere `catch {}`
  // verschluckte jede Fehlermeldung des Backends (z.B. 422 "kein Stripe-Konto
  // verknuepft" fuer Orgs ohne stripe_customer_id) komplett lautlos; (2)
  // `window.open(...)` NACH einem `await` wird von Safari/Chrome haeufig als
  // Popup geblockt, ebenfalls ohne sichtbaren Fehler — beides erklaert exakt
  // Marcos Befund "kein Fehler, keine Rueckmeldung". Fix: Fehler sichtbar machen
  // (Toast) + `window.location.href` statt `window.open` (nicht popup-blockbar,
  // gleiches Muster wie AriaQuotaWidget.tsx handleTopUp).
  const handleTokenOverage = async () => {
    if (overageLoading) return
    setOverageLoading(true)
    try {
      const data = await api.requestTokenOverage((organization.public_token || organization.slug))
      if (data.portal_url) {
        saveSessionForExternalNavigate()
        window.location.href = data.portal_url
      } else {
        showToast('error', 'Token-Buchung konnte nicht geöffnet werden.')
      }
    } catch (err: any) {
      console.error('[Dashboard] requestTokenOverage failed:', err)
      showToast('error', err?.response?.data?.error || 'Token-Buchung konnte nicht geöffnet werden.')
    } finally {
      setOverageLoading(false)
    }
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Guten Morgen'
    if (hour < 18) return 'Guten Tag'
    return 'Guten Abend'
  }

  // --- Personal-Mode-Ableitungen (TQ #6933) -------------------------------
  // Aria-Buchung + "neuer User" werden aus den Cortex-allowed_modules abgeleitet
  // (gleiche Quelle wie die Aria-Token-Karte). hasAria = Modul 'aria' gebucht;
  // isNewUser = noch gar kein Modul gebucht → Starter-Guide zeigen.
  const firstName = user?.firstName || user?.email?.split('@')[0] || ''
  const personalModules = (ariaTokenBudget?.allowed_modules ?? []).map(m => m.toLowerCase())
  const hasAria = personalModules.includes('aria')
  const isNewUser = personalModules.length === 0

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>{getGreeting()}</h1>
          <p className="page-subtitle">Dashboard wird geladen...</p>
        </div>
        <div className="stats-grid">
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        {personal ? (
          <div>
            <h1 style={{ marginBottom: '0.25rem' }}>Willkommen{firstName ? `, ${firstName}` : ''} 👋</h1>
            <p className="page-subtitle">
              Dein persönliches IT-SCE Konto. Aria ist deine KI-Assistentin und hilft dir bei allem,
              was du buchst und nutzt.
            </p>
          </div>
        ) : (
          <div>
            <p className="greeting">{getGreeting()}</p>
            <h1>{organization.name}</h1>
          </div>
        )}
      </div>

      {/* TQ #6933: Starter-Guide für Neuregistrierte (nur Personal-Mode, kein Modul gebucht) */}
      {personal && isNewUser && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
          border: '1px solid #fcd34d',
          borderRadius: 12,
          padding: '1.25rem 1.5rem',
          marginBottom: '2rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }} aria-hidden>🎯</span>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Starter-Guide — los geht's</h2>
          </div>
          <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#78350f' }}>
            Du bist neu hier? In drei Schritten zu deinem ersten Service:
          </p>
          <ol style={{ margin: '0 0 0.5rem 1.25rem', padding: 0, fontSize: '0.9rem', color: '#92400e' }}>
            <li style={{ marginBottom: '0.4rem' }}>
              <strong>Service im Marktplatz wählen</strong> — Mailbox 1,49€/Mo, Aria-Assistentin 12€/Mo, Storage 4,99€/Mo …
            </li>
            <li style={{ marginBottom: '0.4rem' }}>
              <strong>Buchen mit Stripe-Checkout</strong> — monatlich kündbar, sofort verfügbar nach Bezahlung.
            </li>
            <li>
              <strong>IT-SCE Hub installieren</strong> — Container-App für Mac/Win mit Aria-Chat, KI-Aufträge, Support.
            </li>
          </ol>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Link to="/marketplace" style={{
              padding: '0.5rem 1rem', background: '#f59e0b', color: 'white',
              textDecoration: 'none', borderRadius: 6, fontSize: '0.9rem', fontWeight: 500,
            }}>Marktplatz öffnen</Link>
            <Link to="/downloads" style={{
              padding: '0.5rem 1rem', background: 'white', color: '#92400e',
              textDecoration: 'none', borderRadius: 6, fontSize: '0.9rem', fontWeight: 500,
              border: '1px solid #fcd34d',
            }}>IT-SCE Hub herunterladen</Link>
          </div>
        </div>
      )}

      {/* TQ #6933: Aria-CTA + Marktplatz (nur Personal-Mode). Aria-Quota selbst
          steht in der Stat-Card unten — hier nur die Discover/Open-CTA. */}
      {personal && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.25rem',
          marginBottom: '2rem',
        }}>
          {hasAria ? (
            <Link to="cortex" style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white',
              padding: '1.5rem', borderRadius: 12, textDecoration: 'none', display: 'block',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🧠</div>
              <h2 style={{ margin: 0, marginBottom: '0.5rem' }}>Aria öffnen</h2>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>Deine KI-Assistentin. Frag sie was du brauchst.</p>
            </Link>
          ) : (
            <Link to="/marketplace" style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white',
              padding: '1.5rem', borderRadius: 12, textDecoration: 'none', display: 'block',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🧠</div>
              <h2 style={{ margin: 0, marginBottom: '0.5rem' }}>Aria entdecken</h2>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>Deine KI-Assistentin für IT-SCE Services. Im Marktplatz buchen.</p>
            </Link>
          )}
          <Link to="/marketplace" style={{
            background: '#fff', border: '1px solid #e5e7eb', padding: '1.5rem',
            borderRadius: 12, textDecoration: 'none', color: 'inherit', display: 'block',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🛒</div>
            <h2 style={{ margin: 0, marginBottom: '0.5rem' }}>Marktplatz</h2>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>Alle Services die IT-SCE anbietet — buchen oder erweitern.</p>
          </Link>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        {/* Mitglieder-Karte: im Personal-Mode mit member_count<=1 ausgeblendet (TQ #6933) */}
        {!(personal && (dashboard?.member_count ?? 0) <= 1) && (
        <div className="stat-card">
          <div className="stat-icon-wrapper members">
            <UsersIcon size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.member_count || 0}</span>
            <span className="stat-label">Mitglieder</span>
          </div>
          <Link to="members" className="stat-link">
            <ChevronRightIcon size={20} />
          </Link>
        </div>
        )}

        <div className="stat-card">
          <div className="stat-icon-wrapper services">
            <ServiceIcon size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.service_count || 0}</span>
            <span className="stat-label">Aktive Services</span>
          </div>
          <Link to="services" className="stat-link">
            <ChevronRightIcon size={20} />
          </Link>
        </div>

        {dashboard?.license_count !== undefined && dashboard.license_count > 0 && (
          <div className="stat-card">
            <div className="stat-icon-wrapper" style={{ background: '#fef3c7', color: '#92400e' }}>
              <KeyIcon size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{dashboard.license_count}</span>
              <span className="stat-label">Aktive Lizenzen</span>
            </div>
            <Link to="services#lizenzen" className="stat-link">
              <ChevronRightIcon size={20} />
            </Link>
          </div>
        )}

        {/* TQ #8000: Rollen-Karte nur mit Org-Kontext. Im Personal-Modus
            (with.it-sce.de) gibt es keine Org-Rolle -> "Inhaber" wäre sinnlos. */}
        {!personal && (
          <div className="stat-card role-card">
            <div className="stat-icon-wrapper role">
              <UserIcon size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value role-value">
                {getRoleLabel(dashboard?.your_role || organization.member_role)}
              </span>
              <span className="stat-label">{getRoleDescription(dashboard?.your_role || organization.member_role)}</span>
            </div>
          </div>
        )}

        {/* TQ #6933 Regression-Fix 2026-06-03: Aria-Token-Karte nur wenn 'aria'
            in allowed_modules. Sonst wurde sie immer angezeigt sobald
            ariaTokenBudget-API antwortete (auch mit budget=0), was im
            Personal-Mode für nicht-Aria-Nutzer falsch ist. Gating analog
            der CTA-Karte oben (Z277). */}
        {hasAria && ariaTokenBudget !== null && (
          <div className={`stat-card aria-budget-card aria-budget-fullwidth${ariaTokenBudget.warning ? ' aria-budget-warning' : ''}`}>
            <div className="stat-icon-wrapper aria-budget-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="24" height="24">
                <path d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="stat-content aria-budget-content">
              <div className="aria-budget-header">
                <span className="stat-label">Aria-Tokens ({ariaTokenBudget.month})</span>
                {ariaTokenBudget.warning && (() => {
                  // TQ #6109: dynamische Badge statt hardcoded '80% erreicht'.
                  // percent_used ist Fraktion (0.0–1.0+), Backend warnt bereits ab >= 80%.
                  const pct = Math.round(ariaTokenBudget.percent_used * 100)
                  const isOverLimit = pct >= 100
                  const isCritical = pct >= 90
                  const label = isOverLimit
                    ? `${pct}% — Limit überschritten`
                    : `${pct}% erreicht`
                  const cls = isOverLimit
                    ? 'aria-budget-badge aria-budget-badge-over'
                    : isCritical
                      ? 'aria-budget-badge aria-budget-badge-critical'
                      : 'aria-budget-badge'
                  return (
                    <span
                      className={cls}
                      title={`Stand: ${new Date().toLocaleString('de-DE')}`}
                    >
                      {label}
                    </span>
                  )
                })()}
              </div>
              <span className="stat-value aria-budget-numbers">
                {(ariaTokenBudget.sonnet_used / 1000).toFixed(0)}k
                <span className="aria-budget-of"> / {(ariaTokenBudget.sonnet_budget / 1000).toFixed(0)}k</span>
              </span>
              {/* TQ #5133 — Aufsplittung pro Modell-Family wenn vorhanden */}
              {(ariaTokenBudget.haiku_used !== undefined && ariaTokenBudget.haiku_used > 0) || (ariaTokenBudget.opus_used !== undefined && ariaTokenBudget.opus_used > 0) ? (
                <div style={{
                  display: 'flex', gap: 12, fontSize: '0.7rem',
                  color: '#92400e', margin: '4px 0 8px',
                }}>
                  {ariaTokenBudget.haiku_used !== undefined && ariaTokenBudget.haiku_used > 0 && (
                    <span>⚡ Haiku: {(ariaTokenBudget.haiku_used / 1000).toFixed(1)}k</span>
                  )}
                  <span>✨ Sonnet: {((ariaTokenBudget.sonnet_used - (ariaTokenBudget.haiku_used ?? 0) - (ariaTokenBudget.opus_used ?? 0)) / 1000).toFixed(1)}k</span>
                  {ariaTokenBudget.opus_used !== undefined && ariaTokenBudget.opus_used > 0 && (
                    <span>👑 Opus: {(ariaTokenBudget.opus_used / 1000).toFixed(1)}k</span>
                  )}
                </div>
              ) : null}
              <div className="aria-budget-bar-track">
                <div
                  className="aria-budget-bar-fill"
                  style={{ width: `${Math.min(ariaTokenBudget.percent_used * 100, 100).toFixed(1)}%` }}
                />
              </div>
              {personal && (() => {
                const remaining = Math.max(0, ariaTokenBudget.sonnet_budget - ariaTokenBudget.sonnet_used)
                const label = remaining >= 1_000_000
                  ? `${(remaining / 1_000_000).toFixed(1).replace('.', ',')} Mio Tokens verbleibend`
                  : `${(remaining / 1000).toFixed(0)}k Tokens verbleibend`
                return <div className="aria-budget-remaining">{label}</div>
              })()}
              {ariaTokenBudget.allowed_modules.length > 0 && (
                <div className="aria-budget-modules">
                  {ariaTokenBudget.allowed_modules.map(m => (
                    <span key={m} className="aria-budget-module-tag">{m}</span>
                  ))}
                </div>
              )}
              {(() => {
                // TQ #10852 Done-Kriterium 2: Sichtbarkeitslogik (WER darf) bleibt
                // unveraendert (Coordinator-Weisung "nicht anfassen") — nur WIE
                // Ineligibilitaet kommuniziert wird aendert sich: von "Button fehlt
                // komplett" zu "Button sichtbar, disabled, mit erklaerendem Tooltip".
                const canBook = organization.member_role === 'owner' || organization.member_role === 'admin'
                return (
                  <button
                    className="aria-budget-overage-btn"
                    onClick={handleTokenOverage}
                    disabled={!canBook || overageLoading}
                    title={!canBook ? 'nur Owner/Admin mit Aria-Modul' : undefined}
                  >
                    {overageLoading ? 'Wird geöffnet…' : '+ Token-Paket buchen'}
                  </button>
                )
              })()}
            </div>
            <Link to="cortex" className="stat-link">
              <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        )}
      </div>

      {/* Umverdrahtet 23.09.2026 (platform-workspace) von Typ 1 (Cortex-
          Aria-Workspace-Quota, ariaTokenBudget) auf Typ 2 (echtes ERPNext-
          Kundenkontingent, tokenQuota) — Patric-Entscheidung, siehe
          ADR-Cockpit-Live-Daten-und-Token-Einheit.md Entscheidung 2:
          "es gibt nur IT-SCE-Token", Cortex ist an dieser Anzeige nicht
          beteiligt. Zeigt sich sobald ein Token-Kontingent-Dokument fuer den
          Kunden existiert (auch wenn 0 Mio verfuegbar — ehrlicher Leerstand
          statt Ausblenden), blendet sich nur aus wenn die Anfrage fehlschlaegt
          (kein verknuepfter ERPNext-Kunde / Read-Bot nicht konfiguriert). */}
      {tokenQuota === null && tokenQuotaForbidden && (
        <p role="status" style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>{tokenQuotaForbidden}</p>
      )}
      {tokenQuota !== null && (() => {
        // Kontostand-Anzeige (Patric 03.10.2026): Saldo mit Vorzeichen, kein "ueberzogen". Kein
        // Aria-Stopp bei Stand <= 0 (01.10.2026) — das Konto darf ins Minus, Skala/Grenze siehe
        // utils/tokenAccount.ts (TQ #380).
        const account = tokenAccountView(tokenQuota.verbleibend_mio, tokenQuota.gesamt_mio)
        const isDebit = account.mode === 'debit'
        const availableMio = Math.max(0, tokenQuota.verbleibend_mio)
        const totalMio = tokenQuota.gesamt_mio
        const percentRemaining = account.fillPercent
        const hours = (availableMio * 1_000_000) / TOKENS_PER_HOUR
        const isLow = !isDebit && totalMio > 0 && percentRemaining <= 20
        const isEmpty = !isDebit && totalMio > 0 && availableMio <= 0
        return (
          <div className={`token-quota-row${isLow ? ' token-quota-row-warning' : ''}${isDebit ? ' token-quota-row-debit' : ''}`}>
            <div className="token-quota-header">
              <span className="token-quota-label">Token-Kontingent ({tokenQuota.name})</span>
              <span className="token-quota-value">
                {isDebit
                  ? `−${formatTokenAmount(-account.balanceMio * 1_000_000)}`
                  : `${formatTokenAmount(availableMio * 1_000_000)} verfügbar`}
                {(isLow || isEmpty) && (
                  <span
                    className={isEmpty ? 'aria-budget-badge aria-budget-badge-over' : 'aria-budget-badge aria-budget-badge-critical'}
                    style={{ marginLeft: 8 }}
                  >
                    {isEmpty ? 'Kontingent aufgebraucht' : `${Math.round(percentRemaining)}% verbleibend`}
                  </span>
                )}
              </span>
            </div>
            {/* Guthaben: Fuellung haengt rechts (leert sich von links nach rechts).
                Minus: rote Fuellung waechst von rechts nach links bis -MINUS_LIMIT_MIO. */}
            {(isDebit || totalMio > 0) && (
              <div className="token-quota-bar-track">
                <div className="token-quota-bar-fill" style={{ width: `${percentRemaining.toFixed(1)}%` }} />
              </div>
            )}
            {isDebit && (
              <div className="token-quota-scale" aria-hidden="true">
                <span>−{MINUS_LIMIT_MIO} Mio Token</span>
                <span>0</span>
              </div>
            )}
            {account.limitReached && (
              <div className="token-quota-overdrawn" role="status">
                Grenze von −{MINUS_LIMIT_MIO} Mio Token erreicht
              </div>
            )}
            <div className="token-quota-footer">
              <span className="token-quota-hours">
                {!isDebit && <>≈ {formatHours(hours)} Stunden Arbeitszeit · </>}1 Stunde = 18 Mio Token
                {/* TQ #337 (25.09.2026): Verknüpfung 1 — Balken verlinkt auf die
                    Token-Historie im Dokumente-Bereich. Personal-Mode hat kein
                    /dokumente (org-only Bereich); der Endpunkt selbst liegt im
                    admin-Tier (org_users.go), Members ohne Owner/Admin-Rolle
                    würden dort nur einen 403 sehen — Link deshalb zusätzlich
                    auf Owner/Admin gegated (gleiche Rollen wie Documents.tsx). */}
                {!personal && (organization.member_role === 'owner' || organization.member_role === 'admin') && (
                  <Link to="billing#token-verlauf" className="token-quota-history-link">
                    Token-Verlauf →
                  </Link>
                )}
              </span>
            </div>
            {!personal && (
              <QuotaBookingsDrilldown slug={organization.public_token || organization.slug} />
            )}
          </div>
        )
      })()}

      {/* TQ #5711: Setup-Fortschritt + Dienste-Status (echte Signale, kein
          fiktives Phasen-Backend). Org-spezifisch — im Personal-Mode hidden (TQ #6933). */}
      {!personal && (
        <OnboardingHealthPanel slug={organization.public_token || organization.slug} />
      )}

      {/* TQ #6933: Personal-Mode Schnellzugriff-Kacheln + Neuigkeiten (aus
          ehemaliger PersonalDashboard.tsx). */}
      {personal && (
        <>
          <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem' }}>Schnellzugriff</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem',
          }}>
            {[
              { to: '/my-resources', icon: '🧩', label: 'Meine Services' },
              { to: '/licenses',     icon: '🔑', label: 'Meine Lizenzen' },
              { to: '/downloads',    icon: '📥', label: 'Downloads' },
              { to: '/billing',      icon: '💳', label: 'Abrechnung' },
              { to: '/settings',     icon: '⚙️', label: 'Einstellungen' },
              { to: '/support',      icon: '💬', label: 'Hilfe & Support' },
            ].map(item => (
              <Link
                key={item.to}
                to={item.to}
                style={{
                  background: '#fff', border: '1px solid #e5e7eb', padding: '1rem',
                  borderRadius: 8, textDecoration: 'none', color: 'inherit',
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                }}
              >
                <span style={{ fontSize: '1.25rem' }}>{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            ))}
          </div>

          {(newsLoading || news.length > 0) && (
            <>
              <h2 style={{ marginTop: '2.5rem', marginBottom: '1rem', fontSize: '1.25rem' }}>Neuigkeiten</h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1rem',
              }}>
                {newsLoading
                  ? [0, 1, 2].map(i => (
                      <article key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem' }}>
                        <div style={{ height: '0.75rem', width: '30%', background: '#f3f4f6', borderRadius: 4, marginBottom: '0.5rem' }} />
                        <div style={{ height: '1rem', width: '70%', background: '#f3f4f6', borderRadius: 4, marginBottom: '0.5rem' }} />
                        <div style={{ height: '3rem', background: '#f9fafb', borderRadius: 4 }} />
                      </article>
                    ))
                  : news.map(post => (
                      <article key={post.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem' }}>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.25rem' }}>{post.category}</div>
                        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>{post.headline}</h3>
                        <p style={{ margin: 0, color: '#4b5563', fontSize: '0.9rem' }}>
                          {post.body}
                          {post.cta_href && post.cta_label && (
                            <> <Link to={post.cta_href} style={{ color: '#6366f1' }}>{post.cta_label}</Link></>
                          )}
                        </p>
                      </article>
                    ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Quick Actions — org-spezifisch (TQ #6933). Das Widget 'Letzte Aktivitaeten' ist entfernt (Patric 15.10.: zeigte interne Aria-Werkzeugaufrufe). */}
      {!personal && (
      <div className="dashboard-grid">
        {/* Quick Actions */}
        <section className="dashboard-section" style={{ gridColumn: '1 / -1' }}>
          <h2>Schnellzugriff</h2>
          <div className="actions-list">
            <Link to="members" className="action-card">
              <div className="action-icon members">
                <UsersIcon size={22} />
              </div>
              <div className="action-content">
                <span className="action-title">Mitglieder verwalten</span>
                <span className="action-desc">Einladen, Rollen ändern, entfernen</span>
              </div>
              <ChevronRightIcon size={20} className="action-arrow" />
            </Link>

            <Link to="services" className="action-card">
              <div className="action-icon services">
                <ServiceIcon size={22} />
              </div>
              <div className="action-content">
                <span className="action-title">Services verwalten</span>
                <span className="action-desc">Gebuchte Services einsehen</span>
              </div>
              <ChevronRightIcon size={20} className="action-arrow" />
            </Link>

            {(organization.member_role === 'owner' || organization.member_role === 'admin') && (
              <Link to="settings" className="action-card">
                <div className="action-icon settings">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="action-content">
                  <span className="action-title">Einstellungen</span>
                  <span className="action-desc">Organisation konfigurieren</span>
                </div>
                <ChevronRightIcon size={20} className="action-arrow" />
              </Link>
            )}
          </div>
        </section>
      </div>
      )}
    </div>
  )
}
